/**
 * FactoryJourneyOrchestrator (AJIP-16.02.009 · §18 · Sole Composition Layer)
 * ---------------------------------------------------------------------
 * Coordinates certified Foundation services and factory-specific
 * command handling. §18.1 separation of concerns is enforced by this
 * file: this class never registers journeys itself (delegates to
 * RegistrationService), never resolves (delegates to ResolutionService),
 * never authorises activation (delegates to ActivationService /
 * ActivationAuthorisationGate), never writes storage (delegates to
 * PersistenceService via engine event bus).
 *
 * Founder Rulings honoured:
 *   F1 — Requires JOURNEYS_ENABLED AND JOURNEYS_FACTORY.
 *   F2 — Registers 'factory' v2 via RegistrationService.replace().
 *   F3 — journeyVersion is integer 2.
 *   F4 — One active journey per (organisationRef, factorySubjectRef).
 *        Multiple principals under one organisation share the same
 *        instance (identity omits principalRef).
 *   F5 — Journey statuses mapped to certified STATES only.
 *   F6 — Deterministic instance identity uses ONLY:
 *          journeyKey :: journeyVersion :: organisationRef :: factorySubjectRef
 *        principalRef and correlationRef are context-only.
 *   F7 — RETURN_FACTORY_STAGE_FOR_CORRECTION with explicit permission
 *        check + transition matrix + correction reason capture.
 *
 * Directive corrections honoured:
 *   Correction 3 — no duplicate factory record; references authoritative
 *                  factory data via factorySubjectRef only.
 *   Correction 4 — no public trust consequence emitted anywhere.
 *   Correction 5 — no customer-facing route created.
 *   Correction 10 — no AI/matching/RFQ/reputation/verification logic.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { buildFactoryDefinition } from './FactoryJourneyDefinition';
import {
  FACTORY_STAGES, FACTORY_COMMANDS, FACTORY_STATUSES,
  STAGE_TO_ENGINE_STATE, STATUS_TO_ENGINE_STATE,
  findForwardTransition, canReturnForCorrection,
} from './FactoryJourneyModel';
import { validateFactoryContext } from './FactoryJourneyContextContract';
import {
  FACTORY_JOURNEY_EVENTS, FactoryJourneyObservability,
} from './FactoryJourneyObservability';
import {
  InvalidFactoryTransitionError,
  FactoryJourneyNotActiveError,
  FactoryInstanceIdentityError,
  FactoryCommandInvalidError,
  FactoryCommandDuplicateError,
  FactorySubjectMismatchError,
  FACTORY_OUTCOME_CODES,
} from './FactoryJourneyErrors';
import {
  evaluateFactoryOwnership, FACTORY_OWNERSHIP_DECISIONS,
} from './FactoryJourneyOwnership';
import { fnv1a } from '../persistence/Hash';

const FACTORY_JOURNEY_ID = 'factory';
const FACTORY_JOURNEY_VERSION = 2;

const _requireEnabled = (svc) => {
  if (!svc._enabled) {
    throw new FlagDisabledError('JOURNEYS_ENABLED and/or JOURNEYS_FACTORY is false. FactoryJourneyOrchestrator is inert.');
  }
};

/**
 * F6 — deterministic journey instance identity.
 *
 * Canonical inputs (documented order):
 *   1. canonical journey key
 *   2. journey version
 *   3. organisationRef
 *   4. factorySubjectRef
 *
 * principalRef and correlationRef are DELIBERATELY EXCLUDED. Multiple
 * authorised principals under the same organisation must resolve the
 * same organisation-factory instance. Correlation ids are request-
 * specific and must never affect resolved instance.
 *
 * Fail-closed on missing organisationRef or factorySubjectRef.
 * Raw inputs are never emitted in the identifier.
 */
const buildFactoryInstanceId = ({ organisationRef, factorySubjectRef }) => {
  const missing = [];
  if (typeof organisationRef !== 'string' || organisationRef.length === 0) missing.push('organisationRef');
  if (typeof factorySubjectRef !== 'string' || factorySubjectRef.length === 0) missing.push('factorySubjectRef');
  if (missing.length > 0) {
    throw new FactoryInstanceIdentityError(
      `Missing required identity input(s): ${missing.join(', ')}`,
    );
  }
  const material = `${FACTORY_JOURNEY_ID}::${FACTORY_JOURNEY_VERSION}::${organisationRef}::${factorySubjectRef}`;
  return `fji_${fnv1a(material)}`;
};

class FactoryJourneyOrchestrator {
  constructor({
    engine, registrationService, discoveryService,
    resolutionService, activationService, journeyCommands,
    persistenceService = null,
    ownershipProvider = null,
    observability = null,
    nowFn = () => Date.now(),
  } = {}) {
    // F1 — both flags required.
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED') && isFeatureEnabled('JOURNEYS_FACTORY');
    if (this._enabled) {
      if (!engine) throw new Error('FactoryJourneyOrchestrator requires engine');
      if (!registrationService) throw new Error('requires registrationService');
      if (!discoveryService) throw new Error('requires discoveryService');
      if (!resolutionService) throw new Error('requires resolutionService');
      if (!activationService) throw new Error('requires activationService');
      if (!journeyCommands) throw new Error('requires journeyCommands');
    }
    this._engine = engine || null;
    this._reg = registrationService || null;
    this._disc = discoveryService || null;
    this._res = resolutionService || null;
    this._act = activationService || null;
    this._cmd = journeyCommands || null;
    this._persistence = persistenceService;
    this._ownershipProvider = ownershipProvider;
    this._obs = observability instanceof FactoryJourneyObservability
      ? observability : new FactoryJourneyObservability();
    this._now = nowFn;
    this._instances = new Map(); // instanceId -> metadata
    this._commandLog = new Map(); // commandId -> instanceId (idempotency)
    this._registered = false;
  }

  get enabled() { return this._enabled; }
  get observability() { return this._obs; }
  static get JOURNEY_ID() { return FACTORY_JOURNEY_ID; }
  static get JOURNEY_VERSION() { return FACTORY_JOURNEY_VERSION; }

  register() {
    _requireEnabled(this);
    const def = buildFactoryDefinition({ version: FACTORY_JOURNEY_VERSION });
    const status = this._reg.getRegistrationStatus(FACTORY_JOURNEY_ID);
    if (status && status.journeyVersion < FACTORY_JOURNEY_VERSION) {
      this._reg.replace(def);
    } else if (!status) {
      this._reg.register(def);
    }
    this._registered = true;
    this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_REGISTRATION_CONFIRMED, {
      journeyId: FACTORY_JOURNEY_ID, journeyVersion: FACTORY_JOURNEY_VERSION,
    });
    return def;
  }

  evaluateOwnership({ principalRef, organisationRef, factorySubjectRef }) {
    _requireEnabled(this);
    const outcome = evaluateFactoryOwnership({
      principalRef, organisationRef, factorySubjectRef,
      provider: this._ownershipProvider,
      nowFn: this._now,
    });
    this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_OWNERSHIP_EVALUATED, {
      principalRef, organisationRef, factorySubjectRef, decision: outcome.decision,
    });
    return outcome;
  }

  buildResolutionRequest({
    requestId, principalRef, organisationRef, factorySubjectRef,
    correlationRef = null, discoveryContext = {},
  }) {
    validateFactoryContext({
      principalRef, organisationRef, factorySubjectRef, correlationRef,
      ...discoveryContext,
    }, { strict: true });
    const instanceId = buildFactoryInstanceId({ organisationRef, factorySubjectRef });
    return {
      requestId,
      correlationRef,
      canonReference: 'AJIP-16.02.009',
      journeyId: FACTORY_JOURNEY_ID,
      journeyVersion: FACTORY_JOURNEY_VERSION,
      principalContext: { principalRef },
      discoveryContext: {
        authenticated: true,
        permissions: [],
        activeFeatureFlags: ['JOURNEYS_ENABLED', 'JOURNEYS_FACTORY'],
        audience: 'authorised-factory',
        completedJourneys: [],
        activeJourneys: [],
        organisationRef, factorySubjectRef,
        ...discoveryContext,
      },
      entryPoint: 'internal-orchestration',
      requiredFeatureFlags: ['JOURNEYS_ENABLED', 'JOURNEYS_FACTORY'],
      activationIntent: true,
      metadata: { organisationRef, factorySubjectRef, instanceId },
    };
  }

  async activate({
    requestId, principalRef, organisationRef, factorySubjectRef,
    correlationRef = null, discoveryContext = {},
  }) {
    _requireEnabled(this);
    this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_ACTIVATION_ATTEMPTED, {
      requestId, principalRef, organisationRef, factorySubjectRef,
    });
    const ownership = this.evaluateOwnership({ principalRef, organisationRef, factorySubjectRef });
    if (ownership.decision !== FACTORY_OWNERSHIP_DECISIONS.AUTHORISED) {
      const code = ownership.decision === FACTORY_OWNERSHIP_DECISIONS.INPUT_INVALID
        ? FACTORY_OUTCOME_CODES.FACTORY_SUBJECT_INVALID
        : ownership.decision === FACTORY_OWNERSHIP_DECISIONS.SUBJECT_UNKNOWN
          ? FACTORY_OUTCOME_CODES.FACTORY_SUBJECT_NOT_FOUND
          : ownership.decision === FACTORY_OWNERSHIP_DECISIONS.DENIED
            ? FACTORY_OUTCOME_CODES.FACTORY_ACTOR_UNAUTHORISED
            : FACTORY_OUTCOME_CODES.FACTORY_RELATIONSHIP_UNCONFIRMED;
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_ACTIVATION_DENIED, {
        requestId, outcomeCode: code, decision: ownership.decision,
      });
      return Object.freeze({
        status: 'REJECTED', outcomeCode: code, reason: 'ownership-not-authorised', ownership,
      });
    }
    const request = this.buildResolutionRequest({
      requestId, principalRef, organisationRef, factorySubjectRef,
      correlationRef, discoveryContext,
    });
    const result = await this._act.activate(request);
    if (result.status === 'ACTIVATED' || result.status === 'RESUMED' || result.status === 'RESTORED') {
      const instanceId = request.metadata.instanceId;
      this._instances.set(instanceId, {
        organisationRef, factorySubjectRef,
        currentStage: FACTORY_STAGES.FACTORY_IDENTITY,
      });
      this._engine.updateStage(FACTORY_JOURNEY_ID, FACTORY_STAGES.FACTORY_IDENTITY);
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_ACTIVATION_ACCEPTED, {
        requestId, instanceId, initialStage: FACTORY_STAGES.FACTORY_IDENTITY,
      });
    } else {
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_ACTIVATION_DENIED, {
        requestId, status: result.status, reason: result.reason,
      });
    }
    return result;
  }

  /**
   * Accept a factory command. Validates:
   *   - command type against alphabet;
   *   - active journey and instance;
   *   - subject consistency;
   *   - ownership authority still holds;
   *   - forward-transition permitted (or backward-correction permitted);
   *   - command idempotency (commandId reuse returns REPLAYED).
   */
  submitCommand(command) {
    _requireEnabled(this);
    const now = this._now();
    // Basic shape checks.
    if (!command || typeof command !== 'object') {
      throw new FactoryCommandInvalidError('Command must be plain object', FACTORY_OUTCOME_CODES.FACTORY_COMMAND_INVALID);
    }
    const {
      commandId, commandType, journeyInstanceId,
      organisationRef, factorySubjectRef, principalRef,
      expectedStage, targetStage = null, correctionReason = null,
      payload = null,
    } = command;
    if (typeof commandId !== 'string' || commandId.length === 0) {
      throw new FactoryCommandInvalidError('commandId required', FACTORY_OUTCOME_CODES.FACTORY_COMMAND_INVALID);
    }
    if (!Object.values(FACTORY_COMMANDS).includes(commandType)) {
      throw new FactoryCommandInvalidError(`Unknown commandType '${commandType}'`, FACTORY_OUTCOME_CODES.FACTORY_COMMAND_INVALID);
    }
    // Idempotency — same commandId returns replay marker without re-mutating.
    if (this._commandLog.has(commandId)) {
      return Object.freeze({
        status: 'REPLAYED', commandId,
        instanceId: this._commandLog.get(commandId),
      });
    }
    // Instance + subject consistency.
    const instance = this._instances.get(journeyInstanceId);
    if (!instance) {
      throw new FactoryJourneyNotActiveError(`No active instance '${journeyInstanceId}'`, FACTORY_OUTCOME_CODES.FACTORY_JOURNEY_NOT_FOUND);
    }
    if (instance.organisationRef !== organisationRef
      || instance.factorySubjectRef !== factorySubjectRef) {
      throw new FactorySubjectMismatchError('Subject/organisation does not match instance', FACTORY_OUTCOME_CODES.FACTORY_SUBJECT_INVALID);
    }
    // Runtime ownership re-check (§25 — never trust prior evaluation blindly).
    const ownership = evaluateFactoryOwnership({
      principalRef, organisationRef, factorySubjectRef,
      provider: this._ownershipProvider, nowFn: this._now,
    });
    if (ownership.decision !== FACTORY_OWNERSHIP_DECISIONS.AUTHORISED) {
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_STAGE_COMMAND_REJECTED, {
        commandId, commandType, reason: 'ownership-not-authorised',
        outcomeCode: FACTORY_OUTCOME_CODES.FACTORY_ACTOR_UNAUTHORISED,
      });
      return Object.freeze({
        status: 'REJECTED', outcomeCode: FACTORY_OUTCOME_CODES.FACTORY_ACTOR_UNAUTHORISED,
        commandId, ownership,
      });
    }
    // Stage expectation.
    if (expectedStage && instance.currentStage !== expectedStage) {
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_STAGE_COMMAND_REJECTED, {
        commandId, commandType, reason: 'stale-expected-stage',
        outcomeCode: FACTORY_OUTCOME_CODES.FACTORY_STATE_CONFLICT,
      });
      return Object.freeze({
        status: 'REJECTED', outcomeCode: FACTORY_OUTCOME_CODES.FACTORY_STATE_CONFLICT,
        commandId, currentStage: instance.currentStage, expectedStage,
      });
    }
    // Terminal protection (§16.3).
    if (instance.currentStage === FACTORY_STAGES.FACTORY_JOURNEY_READY
        && commandType !== FACTORY_COMMANDS.RESUME_FACTORY_JOURNEY) {
      throw new InvalidFactoryTransitionError(
        'Post-completion mutation prohibited',
        FACTORY_OUTCOME_CODES.FACTORY_JOURNEY_ALREADY_COMPLETED,
      );
    }
    // F7 — authorised backward navigation.
    if (commandType === FACTORY_COMMANDS.RETURN_FACTORY_STAGE_FOR_CORRECTION) {
      if (!targetStage) {
        throw new FactoryCommandInvalidError('targetStage required for correction', FACTORY_OUTCOME_CODES.FACTORY_COMMAND_INVALID);
      }
      if (typeof correctionReason !== 'string' || correctionReason.trim().length === 0) {
        throw new FactoryCommandInvalidError('correctionReason required for correction', FACTORY_OUTCOME_CODES.FACTORY_COMMAND_INVALID);
      }
      if (!canReturnForCorrection(instance.currentStage, targetStage)) {
        throw new InvalidFactoryTransitionError(
          `Cannot return from '${instance.currentStage}' to '${targetStage}'`,
          FACTORY_OUTCOME_CODES.FACTORY_TRANSITION_NOT_PERMITTED,
        );
      }
      // Explicit permission check — permission must be present in the
      // runtime-evaluated ownership grant.
      if (!ownership.grantedPermissions.includes('factory:journey:correct')) {
        throw new FactoryCommandInvalidError(
          'Missing permission factory:journey:correct',
          FACTORY_OUTCOME_CODES.FACTORY_ACTOR_UNAUTHORISED,
        );
      }
      const fromStage = instance.currentStage;
      instance.currentStage = targetStage;
      this._engine.updateStage(FACTORY_JOURNEY_ID, targetStage);
      this._commandLog.set(commandId, journeyInstanceId);
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_STAGE_RETURNED_FOR_CORRECTION, {
        commandId, journeyInstanceId, fromStage, toStage: targetStage,
        correctionReason, at: now,
      });
      return Object.freeze({
        status: 'ACCEPTED', direction: 'backward-correction',
        commandId, fromStage, toStage: targetStage,
      });
    }
    // START_FACTORY_JOURNEY is a no-op idempotency probe once active.
    if (commandType === FACTORY_COMMANDS.START_FACTORY_JOURNEY) {
      this._commandLog.set(commandId, journeyInstanceId);
      return Object.freeze({
        status: 'ACCEPTED', commandId,
        currentStage: instance.currentStage, note: 'already-active',
      });
    }
    if (commandType === FACTORY_COMMANDS.RESUME_FACTORY_JOURNEY) {
      this._commandLog.set(commandId, journeyInstanceId);
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_RESUMED, {
        commandId, journeyInstanceId, currentStage: instance.currentStage,
      });
      return Object.freeze({
        status: 'ACCEPTED', commandId,
        currentStage: instance.currentStage,
      });
    }
    if (commandType === FACTORY_COMMANDS.COMPLETE_FACTORY_FOUNDATION) {
      if (instance.currentStage !== FACTORY_STAGES.OPERATIONAL_READINESS) {
        throw new InvalidFactoryTransitionError(
          'COMPLETE requires stage OPERATIONAL_READINESS',
          FACTORY_OUTCOME_CODES.FACTORY_REQUIREMENTS_INCOMPLETE,
        );
      }
    }
    // Forward transition.
    const transition = findForwardTransition(instance.currentStage, commandType);
    if (!transition) {
      throw new InvalidFactoryTransitionError(
        `No forward transition from '${instance.currentStage}' on command '${commandType}'`,
        FACTORY_OUTCOME_CODES.FACTORY_TRANSITION_NOT_PERMITTED,
      );
    }
    const fromStage = instance.currentStage;
    instance.currentStage = transition.to;
    if (transition.engineTransition === 'complete') {
      this._engine.complete(FACTORY_JOURNEY_ID);
    }
    this._engine.updateStage(FACTORY_JOURNEY_ID, transition.to);
    this._commandLog.set(commandId, journeyInstanceId);
    this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_STAGE_COMMAND_ACCEPTED, {
      commandId, commandType, fromStage, toStage: transition.to,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
      payloadPresent: payload !== null && payload !== undefined,
    });
    if (transition.to === FACTORY_STAGES.FACTORY_JOURNEY_READY) {
      this._obs.emit(FACTORY_JOURNEY_EVENTS.FACTORY_JOURNEY_COMPLETED, {
        commandId, journeyInstanceId, completedAt: new Date(now).toISOString(),
      });
    }
    return Object.freeze({
      status: 'ACCEPTED', direction: 'forward',
      commandId, fromStage, toStage: transition.to,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
    });
  }

  cancel({ requestId, principalRef, correlationRef = null }) {
    _requireEnabled(this);
    const r = this._cmd.cancel({
      requestId, journeyId: FACTORY_JOURNEY_ID, principalRef, correlationRef,
    });
    return r;
  }

  // Utility helpers (read-only) for tests + diagnostics.
  hasInstance(instanceId) { return this._instances.has(instanceId); }
  instanceCount() { return this._instances.size; }
  isRegistered() { return this._registered; }
  currentStageOf(instanceId) { return this._instances.get(instanceId)?.currentStage || null; }
  statusFor(engineState) {
    for (const [status, mapped] of Object.entries(STATUS_TO_ENGINE_STATE)) {
      if (mapped === engineState) return FACTORY_STATUSES[status] || null;
    }
    return null;
  }
}

export {
  FactoryJourneyOrchestrator,
  buildFactoryInstanceId,
  FACTORY_JOURNEY_ID,
  FACTORY_JOURNEY_VERSION,
};
